import { appStudioUtils, biLogger } from '#packages/util';
import { privateAppsCreateNewAppClick } from '@wix/bi-logger-blocks-consumption/v2';
import type { AppData } from '..';
import type DS from '@wix/document-services-types';
import * as stateManagement from '#packages/stateManagement';
import { translate } from '#packages/i18n';
import * as util from '#packages/util';
import type { Dispatch } from 'types/redux';

export enum OpenBlocksEditorOrigins {
  EMPTY_STATE = 'empty state',
  PRIVATE_APPS_PANEL = 'private apps panel',
}

export const openBlocksEditor = (
  origin: OpenBlocksEditorOrigins,
  isStudioAccount: boolean = false,
) => {
  biLogger.report(
    privateAppsCreateNewAppClick({
      builderType: 'classic',
      origin,
    }),
  );
  appStudioUtils.openBlocks({
    mode: appStudioUtils.AppStudioMode.App,
    isWixUser: true,
    httpReferrer: appStudioUtils.httpReferrerOptions.PrivateAppPanel,
    isStudioAccount,
  });
};

export const isMinorUpdatesOn = (version?: string) =>
  version?.substring(0, 1) === '^';

export const resetTestVersion = (version?: string) =>
  version ? `${version.substring(0, version.lastIndexOf('.') + 1)}0` : '';

const openRefreshSiteMessage = (
  title: string,
  message: string,
  dispatch: Dispatch,
) => {
  dispatch(
    stateManagement.panels.actions.updateOrOpenPanel(
      'panels.messagePanels.genericMessage',
      {
        title,
        message,
        confirmLabel: translate(
          'responsive-blocks-packages.Dashboard_App_Delete_Modal_cta',
        ),
        onConfirm: () => {
          util.windowInteractionUtils.reloadPageWithoutQuestion();
        },
        close: () => {
          dispatch(
            stateManagement.panels.actions.closePanelByNameAction(
              'panels.messagePanels.genericMessage',
            ),
          );
        },
        dataHook: 'refresh-site',
      },
      true,
    ),
  );
};

const openRefreshAppMessageAfterInstall = (dispatch: Dispatch) => {
  const title = translate(
    'responsive-blocks-packages.Dashboard_App_Delete_Modal_Header',
  );
  const message = translate(
    'responsive-blocks-packages.Dashboard_App_Delete_Modal_Text',
  );
  openRefreshSiteMessage(title, message, dispatch);
};

export const openRefreshAppMessageAfterUpdate = (
  appName: string,
  dispatch: Dispatch,
) => {
  const title = translate(
    'responsive-blocks-packages.Dashboard_App_Update_Modal_Header',
    {
      appName,
    },
  );
  const message = translate(
    'responsive-blocks-packages.Dashboard_App_Update_Modal_Text',
    {
      appName,
    },
  );
  openRefreshSiteMessage(title, message, dispatch);
};

export const openInstallLinkAndShowRefreshSiteMessage = (
  msid: string,
  appDefinitionId: string,
  version: string,
  dispatch: Dispatch,
) => {
  const url = getInstallDashboardAppURL(msid, appDefinitionId, version);

  openRefreshAppMessageAfterInstall(dispatch);
  window.open(url, DASHBOARD_APP_INSTALL_URL);
  return;
};

export const openUpdateLinkAndShowRefreshSiteMessage = (
  msid: string,
  appDefinitionId: string,
  version: string,
  appName: string,
  dispatch: Dispatch,
) => {
  const url = getInstallDashboardAppURL(msid, appDefinitionId, version);

  openRefreshAppMessageAfterUpdate(appName, dispatch);
  window.open(url, DASHBOARD_APP_INSTALL_URL);
  return;
};

const getInstallDashboardAppURL = (
  msId: string,
  appId: string,
  version: string,
) => {
  const url = new URL('https://manage.wix.com/installer/install-seamless');
  url.searchParams.set('appId', appId);
  url.searchParams.set('intent', 'partners');
  url.searchParams.set('metaSiteId', msId);
  url.searchParams.set('platform', 'dashboard');
  url.searchParams.set('version', version);
  return url;
};

export const DASHBOARD_APP_INSTALL_URL = 'dashboardAppInstall';

export const parseAppVersion = (version: string = '') =>
  version
    .split('.')
    .reverse()
    .filter((_, idx) => idx)
    .reverse()
    .join('.');

export const isAlphaApp = (blocksVersion: string) =>
  !blocksVersion || blocksVersion === '1.0.0';

export const removeCaret = (version: string) => version.replace('^', '');

export const isAutoUpdatePending = (
  currentVersion: string,
  latestVersion: string,
  installedVersion: string,
) => {
  if (
    util.appStudioUtils.isTestVersion(installedVersion) &&
    currentVersion < latestVersion
  ) {
    return true;
  }
  const currVersionObj = appStudioUtils.getVersion(currentVersion);
  const latestVersionObj = appStudioUtils.getVersion(latestVersion);
  return (
    isMinorUpdatesOn(installedVersion) &&
    currVersionObj.major === latestVersionObj.major &&
    currVersionObj.minor < latestVersionObj.minor
  );
};

export const trimSemanticVersion = (
  semanticVersion: string | undefined,
): string | undefined =>
  semanticVersion ? removeCaret(semanticVersion) : undefined;

export const getAppVersion = (app: DS.AppData): string | undefined =>
  trimSemanticVersion(app.appFields?.semanticVersion);

// Applicable only to "available" apps
export const isTestVersionOnlyApp = (app: AppData): boolean =>
  app.latestVersion === '0.0.0';
